import { type FC, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';

import { Link } from 'dibs-elements/exports/Link.tsx';

import styles from './ProfileHeaderData.scss';

import { Header } from 'dibs-elements/exports/Header.tsx';
import GetHelpModalLazy from 'dibs-contact-1stdibs/exports/GetHelpModalLazy.tsx';

import ProfileIcon from './ProfileIcon.tsx';
import ProfileLocation from './ProfileLocation.tsx';
import ProfileTabs from './ProfileTabs.tsx';
import ProfileContactInfo from './ProfileContactInfo.tsx';
import ProgressStatus from './ProgressStatus.tsx';
import ProfileFollowContact from './ProfileFollowContact.tsx';

import { type ProfileHeaderData_tradeFirm$key } from './__generated__/ProfileHeaderData_tradeFirm.graphql.ts';
import { type PageType } from '../constants.ts';

type Props = {
    tradeFirm: ProfileHeaderData_tradeFirm$key;
    isEditMode: boolean;
    onChangeTab: (pageType: PageType) => void;
    currentPage: PageType;
};

export const ProfileHeaderData: FC<Props> = ({
    tradeFirm: tradeFirmRef,
    isEditMode,
    onChangeTab,
    currentPage,
}) => {
    const tradeFirm = useFragment(
        graphql`
            fragment ProfileHeaderData_tradeFirm on TradeFirmType {
                ...ProfileFollowContact_tradeFirm @skip(if: $isEditMode)
                ...ProgressStatus_tradeFirm @include(if: $isEditMode)
                ...ProfileLocation_tradeFirm
                ...ProfileTabs_tradeFirm
                ...ProfileContactInfo_tradeFirm
                ...ProfileIcon_tradeFirm
                name
            }
        `,
        tradeFirmRef
    );
    const [isModalOpen, setModalOpen] = useState(false);
    return (
        <>
            <div className={styles.wrapper}>
                <ProfileIcon isEditMode={isEditMode} tradeFirm={tradeFirm} />
                <div className={styles.profileDataWrapper}>
                    <div className={styles.nameWrapper}>
                        <div className={styles.name}>
                            <Header title={tradeFirm?.name} size="large" htmlElementType="h1" />
                        </div>
                        {isEditMode && (
                            <Link onClick={() => setModalOpen(true)} dataTn="update-name">
                                <FormattedMessage
                                    id="abt.designProfile.header.updateNameCta"
                                    defaultMessage="Update Name"
                                />
                                <GetHelpModalLazy
                                    isOpen={isModalOpen}
                                    onClose={() => setModalOpen(false)}
                                    topicValue="trade1stProgram"
                                    placement="tradeProfile"
                                />
                            </Link>
                        )}
                    </div>
                    <ProfileLocation tradeFirm={tradeFirm} isEditMode={isEditMode} />
                    {isEditMode && (
                        <ProfileContactInfo tradeFirm={tradeFirm} isEditMode={isEditMode} />
                    )}
                </div>
                <div className={styles.desktopSpacer} />
                {isEditMode ? (
                    <ProgressStatus tradeFirm={tradeFirm} />
                ) : (
                    <ProfileFollowContact isEditMode={isEditMode} tradeFirm={tradeFirm} />
                )}
            </div>
            <ProfileTabs
                isEditMode={isEditMode}
                onChangeTab={onChangeTab}
                currentTab={currentPage}
                tradeFirm={tradeFirm}
            />
        </>
    );
};
